import React, { useCallback } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector } from '@/store';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { homeStrings } from '@/constants/residents/strings';
import { formatAsPhoneNumber } from '@/utils/residents/utils';
import { showConfirmWithButtons } from '@/utils/common/alert';

import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { GradientBackground, GlassCard } from '@/components/residents/redesign';

type NavigationProp = StackNavigationProp<RootStackParamList>;

const HP = REDESIGN_SPACING.horizontalPadding;

interface InfoGlassRowProps {
  iconName: string;
  label: string;
  value: string;
  onPress?: () => void;
}

const InfoGlassRow: React.FC<InfoGlassRowProps> = ({ iconName, label, value, onPress }) => {
  const { colors } = useThemeColors();
  const content = (
    <GlassCard borderRadius={12} padding={16} style={styles.infoCard}>
      <View style={[styles.infoIconCircle, { backgroundColor: colors.accent.blueBgLight }]}>
        <Icon name={iconName} size={20} color={colors.text.primary} />
      </View>
      <View style={styles.infoContent}>
        <Text style={[styles.infoLabel, { color: colors.text.subtle }]}>{label}</Text>
        <Text style={[styles.infoValue, { color: colors.text.primary }]}>{value}</Text>
      </View>
      {onPress && (
        <Icon name="chevron-forward" size={18} color={colors.text.subtle} />
      )}
    </GlassCard>
  );

  if (onPress) {
    return (
      <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
        {content}
      </TouchableOpacity>
    );
  }

  return content;
};

export const MobileContactOfficeScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const { user } = useAppSelector((state) => state.auth);
  const property = user?.property;

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleWebsitePress = useCallback((url: string) => {
    navigation.navigate('WebView', { url, title: 'Website' });
  }, [navigation]);

  const handlePhonePress = useCallback((phone: string) => {
    showConfirmWithButtons(
      'Contact',
      'How would you like to contact?',
      'Call',
      'Text',
      () => {
        const phoneUrl = `tel:${phone}`;
        Linking.canOpenURL(phoneUrl).then((supported) => {
          if (supported) {
            Linking.openURL(phoneUrl);
          }
        });
      },
      () => {
        const smsUrl = `sms:${phone}`;
        Linking.canOpenURL(smsUrl).then((supported) => {
          if (supported) {
            Linking.openURL(smsUrl);
          }
        });
      }
    );
  }, []);

  const handleEmailPress = useCallback((email: string) => {
    const emailUrl = `mailto:${email}`;
    Linking.canOpenURL(emailUrl).then((supported) => {
      if (supported) {
        Linking.openURL(emailUrl);
      }
    });
  }, []);

  const handleHandbookPress = useCallback(() => {
    const faqUrl = property?.faqUrl || property?.websites?.handbook;
    if (faqUrl) {
      navigation.navigate('WebView', {
        url: faqUrl,
        title: homeStrings.faqs.title,
      });
    }
  }, [navigation, property]);

  const formatAddress = (address: any): string | null => {
    if (!address) return null;
    if (typeof address === 'string') {
      return address;
    }
    const street = address.street || address.address1 || address.line1;
    const city = address.city;
    const state = address.state || address.stateCode;
    const zipCode = address.zipCode || address.zip || address.postalCode;
    if (street && city && state && zipCode) {
      return `${street}, ${city}, ${state} ${zipCode}`;
    }
    return null;
  };

  const formattedAddress = property ? formatAddress(property.address) : null;
  const hasHandbook = property?.websites?.handbook || property?.faqUrl;

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />

      <BaseResidentMobileScreen
        screenName="Resources"
        darkBackground={true}
        headerVariant="gradient"
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: 40 + insets.bottom },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <BackLink
            label="Back to resources"
            onPress={handleBack}
            color={PRIMITIVE_COLORS.resioBlue}
          />

          <GlassCard
            borderRadius={REDESIGN_SPACING.quickActionRadius}
            padding={REDESIGN_SPACING.cardPadding}
            style={styles.headerCard}
          >
            <View style={styles.headerRow}>
              {property?.photoUrl ? (
                <Image
                  source={{ uri: property.photoUrl }}
                  style={styles.headerImage}
                  resizeMode="cover"
                />
              ) : (
                <View style={[styles.headerIconCircle, { backgroundColor: colors.accent.blueBgLight }]}>
                  <Icon name="chatbubble-outline" size={24} color={colors.text.primary} />
                </View>
              )}
              <View style={styles.headerText}>
                <Text style={[styles.title, { color: colors.text.primary }]}>Contact the office</Text>
                <Text style={[styles.description, { color: colors.text.muted }]}>
                  {property?.marketingName || 'Get in touch with your property management'}
                </Text>
              </View>
            </View>
          </GlassCard>

          {property?.websites?.home && (
            <InfoGlassRow
              iconName="globe-outline"
              label="WEBSITE"
              value={property.websites.home}
              onPress={() => handleWebsitePress(property.websites!.home!)}
            />
          )}

          {property?.phone && (
            <InfoGlassRow
              iconName="call-outline"
              label="PHONE"
              value={formatAsPhoneNumber(property.phone)}
              onPress={() => handlePhonePress(property.phone!)}
            />
          )}

          {property?.email && (
            <InfoGlassRow
              iconName="mail-outline"
              label="EMAIL"
              value={property.email}
              onPress={() => handleEmailPress(property.email!)}
            />
          )}

          {formattedAddress && (
            <InfoGlassRow
              iconName="location-outline"
              label="ADDRESS"
              value={formattedAddress}
            />
          )}

          {hasHandbook && (
            <InfoGlassRow
              iconName="book-outline"
              label="RESIDENT HANDBOOK"
              value="View FAQs and guidelines"
              onPress={handleHandbookPress}
            />
          )}
        </ScrollView>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
    gap: 20,
    paddingTop: 8,
  },
  headerCard: {
    gap: 0,
  },
  headerRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
  },
  headerIconCircle: {
    width: 52,
    height: 52,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerImage: {
    width: 52,
    height: 52,
    borderRadius: 16,
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontSize: 18,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  infoIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoContent: {
    flex: 1,
    gap: 2,
  },
  infoLabel: {
    ...REDESIGN_TYPOGRAPHY.label,
  },
  infoValue: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
});
